import { type FormEvent, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CrmContactChannelsEditor } from "@/components/crm/CrmContactChannelsEditor";
import { apiGet, apiPost } from "@/lib/apiClient";
import {
  assertValidCrmChannelEmails,
  channelOptionsFromLeadFields,
  contactChannelsToApiBody,
  defaultPhoneCountryIdFromLeadFields,
  emptyChannelsState,
} from "@/lib/crmContactChannels";
import {
  addressOptionsFromLeadFields,
  assertValidCrmAddresses,
  defaultAddressCountryIdFromLeadFields,
} from "@/lib/crmContactAddresses";
import { fetchCrmLeadModuleFields } from "@/lib/crmLeadModuleFieldsApi";
import { ArrowLeft } from "lucide-react";

type AccountOption = { id: string; name: string };

export function CrmContactNewPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const presetAccountId = searchParams.get("accountId")?.trim() || "";

  const [accounts, setAccounts] = useState<AccountOption[]>([]);
  const [accountId, setAccountId] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [title, setTitle] = useState("");
  const [channels, setChannels] = useState(() => emptyChannelsState("contact"));
  const [leadFieldSections, setLeadFieldSections] = useState<
    Awaited<ReturnType<typeof fetchCrmLeadModuleFields>>["sections"] | null
  >(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const channelOptions = useMemo(
    () => channelOptionsFromLeadFields(leadFieldSections ?? undefined, "contact_lead", "contact"),
    [leadFieldSections],
  );
  const addressTypeOptions = useMemo(
    () => addressOptionsFromLeadFields(leadFieldSections ?? undefined, "contact_lead"),
    [leadFieldSections],
  );
  const defaultPhoneCountryId = useMemo(
    () => defaultPhoneCountryIdFromLeadFields(leadFieldSections ?? undefined, "contact_lead"),
    [leadFieldSections],
  );
  const defaultAddressCountryId = useMemo(
    () => defaultAddressCountryIdFromLeadFields(leadFieldSections ?? undefined, "contact_lead"),
    [leadFieldSections],
  );

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const [accountsRes, fieldsRes] = await Promise.all([
        apiGet<{ accounts?: { id: string; name: string }[] }>("/api/crm/accounts?fetchAll=true"),
        fetchCrmLeadModuleFields(),
      ]);
      if (cancelled) return;
      if (accountsRes.ok) {
        setAccounts((accountsRes.data?.accounts ?? []).map((a) => ({ id: a.id, name: a.name })));
      }
      setLeadFieldSections(fieldsRes.sections);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (presetAccountId && !accountId) setAccountId(presetAccountId);
  }, [presetAccountId, accountId]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      if (!accountId) throw new Error("Select an account.");
      assertValidCrmChannelEmails(channels, "Contact email");
      assertValidCrmAddresses(channels.addresses, "Contact address");
      const apiChannels = contactChannelsToApiBody(channels);
      const hasName = Boolean([firstName.trim(), lastName.trim()].filter(Boolean).join(" ").trim());
      const hasIdentifier = Boolean(apiChannels.email || apiChannels.linkedinUrl);
      if (!hasName && !hasIdentifier) {
        throw new Error("Add a name plus at least one email or social profile.");
      }
      const res = await apiPost<{ id?: string; contact?: { id?: string } }>("/api/crm/contacts", {
        accountId,
        kind: "person",
        firstName: firstName.trim() || undefined,
        lastName: lastName.trim() || undefined,
        title: title.trim() || undefined,
        email: apiChannels.email || undefined,
        phone: apiChannels.phone || undefined,
        linkedinUrl: apiChannels.linkedinUrl || undefined,
        emails: apiChannels.emails,
        phones: apiChannels.phones,
        socials: apiChannels.socials,
        addresses: apiChannels.addresses,
      });
      const newId = res.data?.contact?.id || res.data?.id;
      if (!res.ok || !newId) throw new Error(res.error || "Failed to create contact.");
      void navigate(
        `/crm/contacts/${encodeURIComponent(newId)}?accountId=${encodeURIComponent(accountId)}`,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to create contact.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <Link
        to="/crm/contacts"
        className="inline-flex items-center gap-2 text-sm font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-300"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        All contacts
      </Link>
      <h1 className="text-2xl font-semibold">Create new contact</h1>
      {error ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">{error}</div>
      ) : null}
      <form onSubmit={(e) => void handleSubmit(e)} className="max-w-2xl space-y-4">
        <select
          required
          value={accountId}
          onChange={(e) => setAccountId(e.target.value)}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-white/20 dark:bg-black/20"
        >
          <option value="">Select account…</option>
          {accounts.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </select>
        <div className="grid gap-3 sm:grid-cols-2">
          <input
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="First name"
            className="rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-white/20 dark:bg-black/20"
          />
          <input
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder="Last name"
            className="rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-white/20 dark:bg-black/20"
          />
        </div>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Title"
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-white/20 dark:bg-black/20"
        />
        <CrmContactChannelsEditor
          value={channels}
          onChange={setChannels}
          emailContext="contact"
          typeOptions={channelOptions}
          defaultPhoneCountryId={defaultPhoneCountryId}
          addressTypeOptions={addressTypeOptions}
          defaultAddressCountryId={defaultAddressCountryId}
        />
        <button
          type="submit"
          disabled={isSubmitting}
          className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500 disabled:opacity-50"
        >
          {isSubmitting ? "Creating…" : "Create contact"}
        </button>
      </form>
    </div>
  );
}
